import { useReducer, useEffect } from "react";
import { useAccountInfo } from "library/hooks/useAccountInfo";
import { useNavigate } from "react-router-dom";
import { useLayout } from "library/hooks/useLayout";
import { Layout } from "../Layout/Layout";
import { ActivateCardExpiryDate } from "./ActivateCardExpiryDate";
import { ActivateCardOneTimeCode } from "./ActivateCardOneTimeCode";
import { ActivateCardSuccess } from "./ActivateCardSuccess";
import { ActivateCardError } from "./ActivateCardError";
import {
  PAGES,
  initialActivateCardState,
  activateCardReducer,
} from "./activateCardReducer";

const pageInfo = {
  [PAGES.EXPIRY_DATE]: {
    heading: "Activate card",
    screenComponent: ActivateCardExpiryDate,
  },
  [PAGES.ONE_TIME_CODE]: {
    heading: "Activate card",
    screenComponent: ActivateCardOneTimeCode,
  },
  [PAGES.SUCCESS]: {
    heading: "Card activated successfully",
    headingIcon: "success",
    screenComponent: ActivateCardSuccess,
  },
  [PAGES.ERROR]: {
    heading: "Activate card",
    screenComponent: ActivateCardError,
  },
};

function ActivateCardScreen({
  screenComponent: ScreenComponent,
  state,
  dispatch,
}) {
  const { scrollToTop } = useLayout();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, ScreenComponent]);

  return <ScreenComponent state={state} dispatch={dispatch} />;
}

function ActivateCard({ location }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(
    activateCardReducer,
    initialActivateCardState,
  );
  const account = location?.state?.account;
  const accountInfo = useAccountInfo(account);

  useEffect(() => {
    if (account) {
      const cardActivationUrl = account.links.find(
        (link) => link.rel === "card_activation",
      )?.href;

      if (cardActivationUrl) {
        dispatch({
          type: "CARD_ACTIVATION_URL_FOUND",
          cardActivationUrl,
        });
      } else {
        dispatch({
          type: "CARD_ACTIVATION_URL_NOT_FOUND",
        });
      }
    } else {
      navigate("/");
    }
  }, [account, navigate]);

  useEffect(() => {
    if (accountInfo !== null) {
      dispatch({ type: "ACCOUNT_INFO_LOADED", account, accountInfo });
    }
  }, [account, accountInfo]);

  if (!account) {
    return null;
  }

  const { page } = state;

  const {
    heading: pageHeading,
    headingIcon: pageHeadingIcon,
    screenComponent,
  } = pageInfo[page];

  return (
    <Layout>
      <Layout.Header icon={pageHeadingIcon}>{pageHeading}</Layout.Header>
      <Layout.Main shouldRenderContent={state.account !== null}>
        <ActivateCardScreen
          screenComponent={screenComponent}
          state={state}
          dispatch={dispatch}
        />
      </Layout.Main>
    </Layout>
  );
}

export { ActivateCard };
